<template>
  <form @submit.prevent="() => {}">
    <FormInputGroup label="Name" for-id="name">
      <FormInput
        id="name"
        ref="nameInput"
        v-model:value="inputName"
        type="text"
        placeholder="Type a name..."
        :state="nameMissing || nameDuplicate ? 'error' : 'default'"
        @update:value="setNameFilled"
      />
      <BaseAlert v-if="nameMissing" state="error">
        Name is required.
      </BaseAlert>
      <BaseAlert v-if="nameDuplicate" state="error">
        A field with this name already exists in this type.
      </BaseAlert>
    </FormInputGroup>

    <FormInputGroup label="Options" for-id="options">
      <FormSelectOptions v-model:options="inputOptions" :colors="colors" />
    </FormInputGroup>

    <CollapsibleSection header="Advanced">
      <FormInputGroup label="Description" for-id="description">
        <FormInput
          id="description"
          v-model:value="desc"
          type="text"
          placeholder="Describe your property..."
          state="default"
        />
      </FormInputGroup>

      <!-- <FormInputGroup label="Default" for-id="default">
        <VSelect
          v-model:value="defaultRelation"
          :clearable="false"
          :searchable="false"
          :options="relationOptions"
        >
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><ChevronDownIcon /></span>
          </template>
        </VSelect>
      </FormInputGroup> -->

      <FormInputGroup label="Constraints" for-id="constraints">
        <BaseToggleButton
          label="Required"
          help="This field is required"
          :checked="required"
          @change="(e) => toggleRequired(e)"
        />
        <BaseToggleButton
          label="Limit to 1"
          help="Only one option can be selected"
          :checked="single"
          @change="(e) => toggleSingle(e)"
        />
      </FormInputGroup>
    </CollapsibleSection>
  </form>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import { CalendarColor, GraphCustomType } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import CollapsibleSection from "@/components/graph/definition/form/CollapsibleSection.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import FormInputGroup from "@/components/graph/definition/form/FormInputGroup.vue";
import FormSelectOptions from "@/components/graph/definition/form/FormSelectOptions.vue";
import { Filled } from "@/components/settings/TypeEdition.vue";

export default defineComponent({
  components: {
    BaseAlert,
    BaseToggleButton,
    CollapsibleSection,
    FormInput,
    FormInputGroup,
    FormSelectOptions,
  },
  props: {
    name: { type: String, required: true },
    description: { type: String, default: "" },
    options: {
      type: Object as PropType<Array<GraphCustomType>>,
      required: true,
    },
    colors: {
      type: Map as PropType<Map<GraphCustomType, CalendarColor>>,
      required: true,
    },
    filled: { type: Object as PropType<Filled>, required: true },
    optional: { type: Boolean, required: true },
    list: { type: Boolean, required: true },
    existingNames: { type: Array as PropType<string[]>, default: () => [] },
  },
  emits: {
    updateName: (_name: string) => true,
    updateDescription: (_description: string) => true,
    updateOptions: (_options: Array<GraphCustomType>) => true,
    setNameFilled: () => true,
    updateOptional: (_optional: boolean) => true,
    updateList: (_list: boolean) => true,
  },
  setup(props, { emit }) {
    const {
      filled,
      name,
      description,
      optional,
      options,
      list,
      existingNames,
    } = toRefs(props);
    const nameMissing = computed<boolean>(() => !filled.value.name);
    const nameDuplicate = computed<boolean>(() =>
      name.value ? existingNames.value.includes(name.value) : false
    );
    const setNameFilled = () => emit("setNameFilled");
    const nameInput = ref<InstanceType<typeof FormInput>>();

    onMounted(() => {
      nameInput.value?.focus();
    });

    const inputName = computed({
      get() {
        return name.value;
      },
      set(newName: string) {
        emit("updateName", newName);
      },
    });
    const inputOptions = computed({
      get() {
        return options.value;
      },
      set(newOptions: Array<GraphCustomType>) {
        emit("updateOptions", newOptions);
      },
    });
    const desc = computed({
      get() {
        return description.value;
      },
      set(newDesc: string) {
        emit("updateDescription", newDesc);
      },
    });
    const required = computed<boolean>({
      get() {
        return !optional.value;
      },
      set(required: boolean) {
        emit("updateOptional", !required);
      },
    });
    const toggleRequired = (requiredStatus: boolean) => {
      required.value = requiredStatus;
    };
    const single = ref(!list.value);
    const toggleSingle = (enabled: boolean) => {
      single.value = enabled;
      emit("updateList", !single.value);
    };

    const relationOptions = ref<Array<{ id: string; label: string }>>([
      { id: "spoon", label: "Spoon" },
      { id: "cad", label: "Cad" },
      { id: "jmq", label: "Jmq" },
      { id: "mefyl", label: "Mefyl" },
    ]);

    const defaultRelation = ref("");

    return {
      nameMissing,
      nameDuplicate,
      setNameFilled,
      defaultRelation,
      desc,
      inputOptions,
      inputName,
      relationOptions,
      required,
      single,
      toggleRequired,
      toggleSingle,
      nameInput,
    };
  },
});
</script>

<style lang="css" scoped>
form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
